import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Users, Shield, User, X, Eye } from 'lucide-react';
import { superAdminRolesAPI } from '../../services/superAdminRolesAPI';
import { usePermissions } from '../../utils/permissionUtils';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { confirmToast } from '../../utils/sonnerDialogs';
import ApproveRequestModal from '../../components/modal/admin/ApproveRequestModal';

const RoleManagement = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [activeTab, setActiveTab] = useState('roles');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedApprovalRequest, setSelectedApprovalRequest] = useState(null);
  const [approvalActionType, setApprovalActionType] = useState('approve');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const queryClient = useQueryClient();
  
  // Check permissions
  const { isSubAccount } = usePermissions();
  const { user: currentUser } = useAuth();

  // Fetch roles data
  const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useQuery({
    queryKey: ['super-admin-roles'],
    queryFn: () => superAdminRolesAPI.getRoles()
  });

  // Fetch permissions data
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['super-admin-permissions'],
    queryFn: () => superAdminRolesAPI.getAvailablePermissions()
  });

  // Fetch stats data
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['super-admin-role-stats'],
    queryFn: () => superAdminRolesAPI.getRoleStats()
  });

  // Fetch pending role approval requests
  const { data: pendingApprovalsData, isLoading: pendingApprovalsLoading } = useQuery({
    queryKey: ['role-pending-approvals', activeTab],
    queryFn: () => superAdminRolesAPI.getPendingRoleApprovals(),
    enabled: activeTab === 'pending-approvals'
  });

  // Fetch role approval history
  const { data: approvalHistoryData, isLoading: approvalHistoryLoading } = useQuery({
    queryKey: ['role-approval-history', activeTab],
    queryFn: () => superAdminRolesAPI.getRoleApprovalHistory(),
    enabled: activeTab === 'approval-history'
  });

  // Mutations
  const createRoleMutation = useMutation({
    mutationFn: (roleData) => superAdminRolesAPI.createRole(roleData),
    onSuccess: (data) => {
      // Check if role was created directly or approval was submitted
      if (data.data?.approvalRequest) {
        queryClient.invalidateQueries(['role-approval-requests']);
        toast.success('Role creation request submitted for approval');
      } else {
        // Role created directly (first role bypassed approval)
        queryClient.invalidateQueries(['super-admin-roles']);
        queryClient.invalidateQueries(['super-admin-role-stats']);
        toast.success('Role created successfully');
      }
      setIsCreateModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create role');
    }
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ roleId, roleData }) => superAdminRolesAPI.updateRole(roleId, roleData),
    onSuccess: () => {
      queryClient.invalidateQueries(['super-admin-roles']);
      queryClient.invalidateQueries(['super-admin-role-stats']);
      toast.success('Role updated successfully');
      setIsEditModalOpen(false);
      setSelectedRole(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update role');
    }
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (roleId) => superAdminRolesAPI.deleteRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries(['super-admin-roles']);
      queryClient.invalidateQueries(['super-admin-role-stats']);
      toast.success('Role deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete role');
    }
  });

  // Role approval mutations
  const approveRoleMutation = useMutation({
    mutationFn: (requestId) => superAdminRolesAPI.approveRoleRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries(['role-pending-approvals']);
      queryClient.invalidateQueries(['role-approval-history']);
      queryClient.invalidateQueries(['super-admin-roles']);
      toast.success('Role approved successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to approve role');
    }
  });

  const rejectRoleMutation = useMutation({
    mutationFn: (requestId) => superAdminRolesAPI.rejectRoleRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries(['role-pending-approvals']);
      queryClient.invalidateQueries(['role-approval-history']);
      toast.success('Role rejected successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reject role');
    }
  });
  

  // Extract data
  const roles = rolesData?.data?.roles || [];
  // Backend returns permissions array directly in data, not nested under a 'permissions' key
  const permissions = Array.isArray(permissionsData?.data?.data) 
    ? permissionsData?.data?.data 
    : (permissionsData?.data?.permissions || []);
  const stats = statsData?.data?.stats || {};

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setIsEditModalOpen(true);
  };

  const handleDeleteRole = async (role) => {
    const confirmed = await confirmToast(`Are you sure you want to delete the role "${role.name}"?`, {
      intent: 'danger',
      confirmLabel: 'Delete role'
    });
    if (confirmed) {
      deleteRoleMutation.mutate(role._id);
    }
  };

  // Role approval handlers
  const handleViewRoleDetails = (request) => {
    setSelectedApprovalRequest(request);
    setShowDetailsModal(true);
  };

  const handleApproveRole = (request) => {
    setSelectedApprovalRequest(request);
    setApprovalActionType('approve');
    setShowApprovalModal(true);
  };

  const handleRejectRole = (request) => {
    setSelectedApprovalRequest(request);
    setApprovalActionType('reject');
    setShowApprovalModal(true);
  };

  const handleApprovalModalApprove = (requestId) => {
    approveRoleMutation.mutate(requestId);
  };

  const handleApprovalModalReject = (requestId, reason) => {
    rejectRoleMutation.mutate(requestId);
  };

  const handleApprovalModalViewDetails = (request) => {
    // TODO: Implement view details modal
    console.log('View role details:', request);
    toast.success('View details functionality coming soon');
  };

  // Check if current user is the initiator of the request
  const isCurrentUserInitiator = (request) => {
    if (!currentUser || !request.initiatorId) return false;
    
    const currentUserId = currentUser.id || currentUser._id;
    const initiatorId = request.initiatorId._id || request.initiatorId;
    
    return currentUserId === initiatorId;
  };

  if (rolesLoading || permissionsLoading || statsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (rolesError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-error-600 mb-4">Error Loading Roles</h2>
          <p className="text-secondary-600">{rolesError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Role Management</h1>
              <p className="text-sm text-gray-500 mt-1">Create and manage custom roles for your sub-accounts</p>
            </div>
            {!isSubAccount && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Create New Role
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Roles</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalRoles || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Roles</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeRoles || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Inactive Roles</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inactiveRoles || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Assigned Sub-Accounts</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalAssigned || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('roles')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'roles'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Roles
          </button>
          <button
            onClick={() => setActiveTab('pending-approvals')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pending-approvals'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Pending Approvals
          </button>
          <button
            onClick={() => setActiveTab('approval-history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'approval-history'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Approval History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'roles' && (
        <div className="bg-white rounded-lg shadow border">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">
                  Role Name
                </th>
                <th className="table-header-cell">
                  Permissions
                </th>
                <th className="table-header-cell">
                  Last Modified
                </th>
                <th className="table-header-cell">
                  Assigned To
                </th>
                <th className="table-header-cell">
                  Status
                </th>
                <th className="table-header-cell">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {roles.map((role) => (
                <tr key={role._id} className="hover:bg-secondary-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-secondary-900">{role.name}</div>
                      <div className="text-sm text-secondary-500">{role.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-info-100 text-info-800">
                      {Array.isArray(role.permissions) ? role.permissions.length : 0} permissions
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-secondary-900">
                      {typeof role.lastModifiedBy === 'object' ? role.lastModifiedBy.email : role.lastModifiedBy}
                    </div>
                    <div className="text-sm text-secondary-500">
                      {formatDate(role.lastModifiedDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-secondary-900">
                      <User className="h-4 w-4 mr-1" />
                      {role.assignedToCount || 0} sub-accounts
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      role.isActive 
                        ? 'bg-success-100 text-success-800' 
                        : 'bg-error-100 text-error-800'
                    }`}>
                      {role.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {!isSubAccount && (
                        <button
                          onClick={() => handleEditRole(role)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      )}
                      {!isSubAccount && (
                        <button
                          onClick={() => handleDeleteRole(role)}
                          className="text-error-600 hover:text-error-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Pending Approvals Tab */}
      {activeTab === 'pending-approvals' && (
        <div className="bg-white rounded-lg shadow border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Pending Role Approvals</h3>
            <p className="text-sm text-gray-600">Review and approve role creation requests</p>
          </div>
          <div className="p-6">
            {pendingApprovalsLoading ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-2 text-gray-500">Loading pending approvals...</p>
              </div>
            ) : pendingApprovalsData?.data?.data?.requests?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">
                        Role Name
                      </th>
                      <th className="table-header-cell">
                        Description
                      </th>
                      <th className="table-header-cell">
                        Permissions
                      </th>
                      <th className="table-header-cell">
                        Initiated By
                      </th>
                      <th className="table-header-cell">
                        Date
                      </th>
                      <th className="table-header-cell">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pendingApprovalsData.data.data.requests.map((request) => (
                      <tr key={request._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {request.targetData?.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {request.targetData?.description || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {request.targetData?.permissions?.length || 0} permissions
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {request.initiatorId?.email || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {new Date(request.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleViewRoleDetails(request)}
                              className="text-primary-600 hover:text-indigo-900"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {(() => {
                              const isInitiator = isCurrentUserInitiator(request);
                              if (isInitiator) return null;
                              return (
                                <>
                                  <button 
                                    onClick={() => handleApproveRole(request)}
                                    className="btn-primary btn-sm"
                                    title="Approve"
                                  >
                                    Approve
                                  </button>
                                  <button 
                                    onClick={() => handleRejectRole(request)}
                                    className="btn-secondary btn-sm"
                                    title="Reject"
                                  >
                                    Reject
                                  </button>
                                </>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-gray-500">
                <Shield className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No pending role approvals</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Approval History Tab */}
      {activeTab === 'approval-history' && (
        <div className="bg-white rounded-lg shadow border">
            <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Role Approval History</h3>
            <p className="text-sm text-gray-600">View all role approval requests and their status</p>
          </div>
          <div className="p-6">
            {approvalHistoryLoading ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-2 text-gray-500">Loading approval history...</p>
              </div>
            ) : approvalHistoryData?.data?.data?.history?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">
                        Role Name
                      </th>
                      <th className="table-header-cell">
                        Description
                      </th>
                      <th className="table-header-cell">
                        Status
                      </th>
                      <th className="table-header-cell">
                        Initiated By
                      </th>
                      <th className="table-header-cell">
                        Initiated At
                      </th>
                      <th className="table-header-cell">
                        Approved/Rejected By
                      </th>
                      <th className="table-header-cell">
                        Action Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {approvalHistoryData.data.data.history.map((request) => (
                      <tr key={request._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {request.targetData?.name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {request.targetData?.description || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            request.status === 'approved' ? 'bg-green-100 text-green-800' :
                            request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {request.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {request.initiatorId?.firstName && request.initiatorId?.lastName ? 
                              `${request.initiatorId.firstName} ${request.initiatorId.lastName}` : 
                              request.initiatorId?.email || 'N/A'
                            }
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.initiatorId?.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {new Date(request.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {request.status === 'approved' && request.approvedBy ? (
                            <div>
                              <div className="text-sm text-gray-900">
                                {request.approvedBy?.firstName && request.approvedBy?.lastName ? 
                                  `${request.approvedBy.firstName} ${request.approvedBy.lastName}` : 
                                  request.approvedBy?.email || 'N/A'
                                }
                              </div>
                              <div className="text-sm text-gray-500">
                                {request.approvedBy?.email}
                              </div>
                            </div>
                          ) : request.status === 'rejected' && request.rejectedBy ? (
                            <div>
                              <div className="text-sm text-gray-900">
                                {request.rejectedBy?.firstName && request.rejectedBy?.lastName ? 
                                  `${request.rejectedBy.firstName} ${request.rejectedBy.lastName}` : 
                                  request.rejectedBy?.email || 'N/A'
                                }
                              </div>
                              <div className="text-sm text-gray-500">
                                {request.rejectedBy?.email}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {request.status === 'approved' && request.approvedAt ? 
                              new Date(request.approvedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 
                              request.status === 'rejected' && request.rejectedAt ? 
                                new Date(request.rejectedAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 
                                '-'
                            }
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center text-gray-500">
                <Shield className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No approval history found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Role Modal */}
      {isCreateModalOpen && (
        <CreateRoleModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          permissions={permissions}
          permissionsLoading={permissionsLoading}
          onCreate={createRoleMutation.mutate}
          isCreating={createRoleMutation.isPending}
        />
      )}

      {/* Edit Role Modal */}
      {isEditModalOpen && selectedRole && (
        <EditRoleModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedRole(null);
          }}
          role={selectedRole}
          permissions={permissions}
          permissionsLoading={permissionsLoading}
          onUpdate={(roleData) => updateRoleMutation.mutate({ roleId: selectedRole._id, roleData })}
          isUpdating={updateRoleMutation.isPending}
        />
      )}

      {/* Approval Request Modal */}
      <ApproveRequestModal
        isOpen={showApprovalModal}
        onClose={() => {
          setShowApprovalModal(false);
          setSelectedApprovalRequest(null);
        }}
        request={selectedApprovalRequest}
        onApprove={handleApprovalModalApprove}
        onReject={handleApprovalModalReject}
        isSubmitting={approveRoleMutation.isPending || rejectRoleMutation.isPending}
        actionType={approvalActionType}
      />

      {/* Role Details Modal */}
      {showDetailsModal && selectedApprovalRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Role Request Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Role Name</label>
                      <p className="text-sm text-gray-900">{selectedApprovalRequest.targetData?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Description</label>
                      <p className="text-sm text-gray-900">{selectedApprovalRequest.targetData?.description || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Status</label>
                      <p className="text-sm text-gray-900">Pending L2 Approval</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Requested At</label>
                      <p className="text-sm text-gray-900">{new Date(selectedApprovalRequest.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</p>
                    </div>
                  </div>
                </div>

                {/* Request Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Request Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Requested By</label>
                      <div className="mt-1 p-3 bg-white rounded-md">
                        <p className="text-sm text-gray-900">
                          {selectedApprovalRequest.initiatorId?.firstName && selectedApprovalRequest.initiatorId?.lastName ? 
                            `${selectedApprovalRequest.initiatorId.firstName} ${selectedApprovalRequest.initiatorId.lastName}` : 
                            selectedApprovalRequest.initiatorId?.email || 'N/A'
                          }
                        </p>
                        <p className="text-sm text-gray-500">
                          {selectedApprovalRequest.initiatorId?.email}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Permissions Count</label>
                      <p className="text-sm text-gray-900">{selectedApprovalRequest.targetData?.permissions?.length || 0} permissions</p>
                    </div>
                  </div>
                </div>

                {/* Permissions */}
                {selectedApprovalRequest.targetData?.permissions && selectedApprovalRequest.targetData.permissions.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Assigned Permissions</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {selectedApprovalRequest.targetData.permissions.map((permission, index) => (
                        <div key={index} className="bg-white px-3 py-2 rounded border text-sm text-gray-700">
                          {permission}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments */}
                {selectedApprovalRequest.comments && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Comments</h4>
                    <p className="text-sm text-gray-900">{selectedApprovalRequest.comments}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// Create Role Modal Component
const CreateRoleModal = ({ isOpen, onClose, permissions, permissionsLoading, onCreate, isCreating }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    isActive: true
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim() && formData.description.trim() && formData.permissions.length > 0) {
      // Send role data - backend will decide if approval is needed
      onCreate(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      permissions: [],
      isActive: true
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Create New Role</h3>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter role name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter role description"
                rows={3}
                required
              />
            </div>
          </div>

          {/* Permissions */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-gray-900">Define Permissions</h4>
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-500">
                  {formData.permissions.length} of {permissions.length} selected
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, permissions: permissions.map(p => p.id) }))}
                    disabled={formData.permissions.length === permissions.length}
                    className="text-xs text-primary-600 hover:text-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, permissions: [] }))}
                    disabled={formData.permissions.length === 0}
                    className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>

            {permissionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-gray-600">Loading permissions...</span>
              </div>
            ) : permissions.length === 0 ? (
              <div className="text-center py-8 border border-gray-200 rounded-lg">
                <div className="text-sm text-gray-500">No permissions available</div>
              </div>
            ) : (
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                {permissions.map((permission) => (
                  <div key={permission.id} className="flex items-start p-3 rounded-lg hover:bg-gray-50 border border-gray-100">
                    <input
                      type="checkbox"
                      id={permission.id}
                      checked={formData.permissions.includes(permission.id)}
                      onChange={() => handlePermissionToggle(permission.id)}
                      className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <label htmlFor={permission.id} className="text-sm font-medium text-gray-700 cursor-pointer">
                        {permission.label}
                      </label>
                      {permission.description && (
                        <p className="text-xs text-gray-500 mt-1">{permission.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !formData.name.trim() || !formData.description.trim() || formData.permissions.length === 0}
              className="btn-primary"
            >
              {isCreating ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Creating...
                </div>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Role Modal Component
const EditRoleModal = ({ isOpen, onClose, role, permissions, permissionsLoading, onUpdate, isUpdating }) => {
  const [formData, setFormData] = useState({
    name: role.name,
    description: role.description,
    permissions: role.permissions || [],
    isActive: role.isActive
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim() && formData.description.trim() && formData.permissions.length > 0) {
      onUpdate(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      name: role.name,
      description: role.description,
      permissions: role.permissions || [],
      isActive: role.isActive
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Edit Role</h3>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter role name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter role description"
                rows={3}
                required
              />
            </div>
          </div>

          {/* Permissions */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-gray-900">Define Permissions</h4>
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-500">
                  {formData.permissions.length} of {permissions.length} selected
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, permissions: permissions.map(p => p.id) }))}
                    disabled={formData.permissions.length === permissions.length}
                    className="text-xs text-primary-600 hover:text-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, permissions: [] }))}
                    disabled={formData.permissions.length === 0}
                    className="text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>

            {permissionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-gray-600">Loading permissions...</span>
              </div>
            ) : permissions.length === 0 ? (
              <div className="text-center py-8 border border-gray-200 rounded-lg">
                <div className="text-sm text-gray-500">No permissions available</div>
              </div>
            ) : (
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                {permissions.map((permission) => (
                  <div key={permission.id} className="flex items-start p-3 rounded-lg hover:bg-gray-50 border border-gray-100">
                    <input
                      type="checkbox"
                      id={permission.id}
                      checked={formData.permissions.includes(permission.id)}
                      onChange={() => handlePermissionToggle(permission.id)}
                      className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <label htmlFor={permission.id} className="text-sm font-medium text-gray-700 cursor-pointer">
                        {permission.label}
                      </label>
                      {permission.description && (
                        <p className="text-xs text-gray-500 mt-1">{permission.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUpdating || !formData.name.trim() || !formData.description.trim() || formData.permissions.length === 0}
              className="btn-primary"
            >
              {isUpdating ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Updating...
                </div>
              ) : (
                'Update Role'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleManagement; 
